import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import OrganizationLogos from '../../components/OrganizationLogos';

const HomePage = () => {
  // SEO metadata based on the SEO strategy document
  const seoData = {
    title: "Dozzy | AI Sleep Consultant for Personalized Baby Sleep Plans",
    description: "Create personalized, evidence-based sleep plans for your baby with Dozzy's AI sleep consultant. Affordable guidance based on Australian sleep guidelines.",
    keywords: [
      "baby sleep consultant", 
      "AI sleep consultant", 
      "personalized sleep plan",
      "infant sleep plan",
      "toddler sleep training",
      "baby sleep problems",
      "sleep training app",
      "affordable sleep consultant"
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Dozzy.au",
      "url": "https://dozzy.au",
      "description": "AI-powered sleep plans personalized to your child's unique needs, combining cutting-edge technology with guidelines referenced from respected Australian resources.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://dozzy.au/search?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }
  };

  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        structuredData={seoData.structuredData}
        canonicalUrl="https://dozzy.au"
        ogType="website"
      />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-5 mb-lg-0">
              <div className="red-badge">
              For exhausted parents
              </div>
              <h1>Sleep-deprived? <span className="text-red">You're not alone.</span></h1>
              
              <p className="mb-4" style={{ color: '#2D3142' }}>We know how hard it is - sleepless nights, conflicting advice, and nothing seems to work. Get a personalized sleep plan based on trusted Australian guidelines, tailored to your child's unique needs. Ready in 5 minutes.</p>
              <div className="d-flex flex-wrap gap-3 mb-4">
                <Link to="/signup" className="btn btn-red btn-lg" onClick={() => window.scrollTo(0, 0)}>Get Your Sleep Plan</Link>
                <a href="#how-it-works" className="btn btn-outline-red btn-lg" onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' });
                }}>Learn More</a>
              </div>
              <div className="d-flex align-items-center">
                <div>
                  {/* Organization Logos */}
                  <OrganizationLogos />
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image">
                <img src="/images/baby-7540912_1280.jpg" alt="Peaceful sleeping baby" className="img-fluid rounded-4 shadow" />
                <div className="floating-badge">
                  <div className="badge-content">
                    <div className="badge-icon">
                      <i className="bi bi-moon-stars"></i>
                    </div>
                    <div className="badge-text">
                      <span className="badge-number">7 days</span>
                      <span className="badge-label">to better sleep</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gift a Sleep Plan Banner */}
      <section className="py-4 gift-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div className="d-flex align-items-center">
                <div className="gift-icon me-3">
                  <i className="bi bi-gift text-red" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <div>
                  <h3 className="mb-1">Give the Gift of Sleep</h3>
                  <p className="mb-0">Perfect for baby showers, exhausted new parents, or anyone desperate for sleep!</p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 text-lg-end mt-3 mt-lg-0">
              <Link to="/gift" className="btn btn-red btn-lg pulse-button" onClick={() => window.scrollTo(0, 0)}>
                <i className="bi bi-gift me-2"></i> Give the Gift of Sleep
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section py-5 bg-primary text-white">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title text-white">Why Parents Love Dozzy.au</h2>
            <p className="mb-0 text-white opacity-75">Science-backed sleep solutions designed for your family</p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-4">
              <div className="feature-card red-accent bg-white">
                <div className="feature-icon red-bg">
                  <i className="bi bi-graph-up-arrow red-icon"></i>
                </div>
                <h3 className="text-dark">Personalized Plans</h3>
                <p className="text-dark">Sleep plans tailored to your child's age, temperament, and specific sleep challenges.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="feature-card purple-accent bg-white">
                <div className="feature-icon purple-bg">
                  <i className="bi bi-shield-check purple-icon"></i>
                </div>
                <h3 className="text-dark">Evidence-Based</h3>
                <p className="text-dark">All recommendations reference guidelines from respected Australian resources like Red Nose.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="feature-card mint-accent bg-white">
                <div className="feature-icon mint-bg">
                  <i className="bi bi-currency-dollar mint-icon"></i>
                </div>
                <h3 className="text-dark">Affordable</h3>
                <p className="text-dark">Get expert guidance at a fraction of the cost of traditional sleep consultants.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">How It Works</h2>
            <p className="mb-0">Three simple steps to better sleep</p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-4">
              <div className="step-card">
                <div className="step-number">1</div>
                <div className="step-icon text-center">
                  <i className="bi bi-clipboard-data"></i>
                </div>
                <h3>Share Information</h3>
                <p>Tell us about your child's age, sleep patterns, and challenges through our friendly questionnaire.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="step-card">
                <div className="step-number">2</div>
                <div className="step-icon text-center">
                  <i className="bi bi-file-earmark-text"></i>
                </div>
                <h3>Get Your Plan</h3>
                <p>Our advanced AI analyzes your inputs and creates a personalized sleep plan based on guidelines referenced from respected Australian resources.</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="step-card">
                <div className="step-number">3</div>
                <div className="step-icon text-center">
                  <i className="bi bi-graph-up"></i>
                </div>
                <h3>See Results</h3>
                <p>Follow your customized plan and watch your child's sleep improve, with adjustments as needed.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials py-5 bg-primary text-white">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title text-white">What Parents Say</h2>
            <p className="mb-0 text-white opacity-75">Real stories from families who've used Dozzy.au</p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-4">
              <div className="testimonial-card bg-white">
                <div className="testimonial-header">
                  
                  <div className="testimonial-author">
                    <h4 className="text-dark">Sarah M.</h4>
                    <p className="text-muted">Mother of a 9-month-old</p>
                  </div>
                </div>
                <div className="testimonial-content">
                  <p className="text-dark">After months of sleepless nights, Dozzy.au's personalized plan helped improve our 9-month-old's sleep patterns. The guidance was clear and supportive!</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="testimonial-card bg-white">
                <div className="testimonial-header">
                  
                  <div className="testimonial-author">
                    <h4 className="text-dark">James T.</h4>
                    <p className="text-muted">Father of a 6-month-old</p>
                  </div>
                </div>
                <div className="testimonial-content">
                  <p className="text-dark">As a first-time dad, I was overwhelmed with conflicting sleep advice. Dozzy.au provided clear, evidence-based guidance that gave me confidence. Our little one's sleep has improved significantly!</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="testimonial-card bg-white">
                <div className="testimonial-header">
                  
                  <div className="testimonial-author">
                    <h4 className="text-dark">Emma K.</h4>
                    <p className="text-muted">Mother of a 2-year-old</p>
                  </div>
                </div>
                <div className="testimonial-content">
                  <p className="text-dark">The personalized approach made all the difference. Our toddler had been struggling with sleep regression, but the tailored plan addressed his specific needs and temperament. Worth every penny!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
          <p className="text-center text-white opacity-75 mt-4" style={{fontSize: '0.875rem'}}>
            Individual results may vary. These testimonials reflect personal experiences.
          </p>
      </section>

      

      {/* CTA Section */}
      <section className="cta-section py-5">
        <div className="container">
          <div className="cta-content text-center">
            <h2>Start sleeping <span className="text-red">better tonight</span></h2>
            <p style={{ color: '#2D3142' }}>Join Australian families who have discovered better sleep with Dozzy.au's personalized guidance.</p>
            <Link to="/signup" className="btn btn-red btn-lg" onClick={() => window.scrollTo(0, 0)}>Get Your Sleep Plan</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
